import { useState, useEffect, useRef } from "react";

import { api_without_cancellation } from "../../../../api/api";

/**
 * useSourceSearch
 *
 * Calls GET /api/firm_case_sources/?search=<term> with 400ms debounce.
 * Returns { results, isLoading } to use in place of filteredCaseSources.
 *
 * Usage:
 *   const { results: searchedSources, isLoading: sourceSearchLoading } = useSourceSearch(search);
 */
const useSourceSearch = (searchTerm) => {
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // clear previous debounce
    if (timerRef.current) clearTimeout(timerRef.current);

    // empty search → clear results, no API call
    if (!searchTerm || searchTerm.trim() === "") {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    timerRef.current = setTimeout(async () => {
      try {
        const res = await api_without_cancellation.get(
          `/api/firm_case_sources/?search=${encodeURIComponent(searchTerm.trim())}`
        );
        setResults(res.data?.results ?? res.data ?? []);
      } catch (e) {
        console.error(e);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 400);

    return () => clearTimeout(timerRef.current);
  }, [searchTerm]);

  return { results, isLoading };
};

export default useSourceSearch;
